import { DialogContent, InputAdornment, Typography, Box } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import { format } from 'date-fns'
import AvatarComponent from '@hapana/shared/web/components/shared/avatar/avatar.component'
import { AVATAR_SIZE } from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import Chip from '@hapana/shared/web/components/shared/chip/chip.component'
import { CHIP_SIZE, CHIP_VARIANT } from '@hapana/shared/web/components/shared/chip/chip.constants'
import DialogLayout from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '@hapana/shared/web/components/shared/dialog/dialog-layout/dialog-layout.constants'
import RadioGroup from '@hapana/shared/web/components/shared/radio-group/radio-group.component'
import RadioButton from '@hapana/shared/web/components/shared/radio-button/radio-button.component'
import { RADIO_TYPES } from '@hapana/shared/web/components/shared/radio-button/radio-button.constants'
import TextField from '@hapana/shared/web/components/shared/text-field/text-field.component'
import Dropdown from '@hapana/shared/web/components/shared/dropdown/dropdown.component'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import BannerAlert from '@hapana/shared/web/components/shared/banner-alert/banner-alert.component'
import { SEVERITY } from '@hapana/shared/web/components/shared/banner-alert/banner-alert.constants'
import { useCreateEquipmentAssignment } from '_hooks/equipment-assignments.hooks'
import AssignEquipmentSuccessAlert from '_components/check-in-widget/modal-check-in/modal-check-in-tabs/manage-equipments-tab/alert-dialogs/assign/assign-equipment-success.alert.component'
import AssignEquipmentErrorAlert from '_components/check-in-widget/modal-check-in/modal-check-in-tabs/manage-equipments-tab/alert-dialogs/assign/assign-equipment-error.alert.component'
import TestEquipmentErrorAlert from '_components/check-in-widget/modal-check-in/modal-check-in-tabs/manage-equipments-tab/alert-dialogs/test/test-equipment-error.alert.component'
import { useGetClient, useGetClientAssignments } from '_hooks/clients.hooks'
import { useGetLatestTanningCheckIn } from '_hooks/check-ins.hooks'
import { useGetEquipmentAssignments } from '_hooks/equipments.hooks'
import useSiteStore from '_stores/site.store'
import { calculateAge } from '@hapana/shared/utils/date.util'
import Skeleton from '@hapana/shared/web/components/shared/skeleton/skeleton.component'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import i18n from '@hapana/shared/web/i18n/index'
import { useSiteSessionTypesByIds } from '_pages/scheduler-settings/session-types/session-types.hooks'

import { AddOnConfirmationDialog } from '../add-on-confirmation-dialog/add-on-confirmation-dialog.component'
import { DEFAULT_VALUES, FIELD_NAMES, TIMER_OPTIONS } from './start-timer-dialog.constants'
import { styles } from './start-timer-dialog.styles'

export const StartTimerDialog = ({
  clientId,
  equipmentId,
  equipment,
  clientName,
  isTestMode,
  onSuccess,
}) => {
  const { t } = useTranslation('checkIn')
  const { popDialog, pushDialog } = useDialog()

  const { mutate: createAssignment, isLoading } = useCreateEquipmentAssignment({
    clientName,
  })

  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const { data: client, isLoading: isLoadingClient } = useGetClient(
    isTestMode ? undefined : clientId,
    siteId
  )
  const { data: assignmentsForClient, isLoading: isLoadingAssignmentsForClient } =
    useGetEquipmentAssignments(
      siteId,
      equipmentId,
      isTestMode ? undefined : clientId,
      true,
      undefined
    )
  const latestAssignmentInEquipment = assignmentsForClient?.[0]

  const { data: clientAssignments, isLoading: isLoadingClientAssignments } =
    useGetClientAssignments(isTestMode ? undefined : clientId, siteId, true, undefined)
  const lastTanning = clientAssignments?.[0]

  const { data: latestTanningCheckInData } = useGetLatestTanningCheckIn(
    isTestMode ? undefined : clientId
  )
  const latestTanningCheckIn = latestTanningCheckInData?.latestTanningCheckIn
  const hasMultipleSessionTypes = (equipment.siteSessionTypeIds?.length ?? 0) > 1
  const { data: sessionTypes, isLoading: isLoadingSessionTypes } = useSiteSessionTypesByIds(
    siteId,
    equipment.siteSessionTypeIds ?? []
  )
  const lastTanningDate = lastTanning?.doneAt || lastTanning?.assignedAt || lastTanning?.createdAt
  // GraphQL String scalar serializes Date objects as ms timestamps (e.g. "1745280000000")
  const parsedLastTanningDate = lastTanningDate
    ? new Date(Number.isFinite(+lastTanningDate) ? +lastTanningDate : lastTanningDate)
    : null

  const validationSchema = useMemo(() => {
    const { minTimerMinutes, maxTimerMinutes, minDelayMinutes, maxDelayMinutes } = equipment

    return z
      .object({
        [FIELD_NAMES.TIMER_OPTION]: z
          .string()
          .refine((val) => Object.values(TIMER_OPTIONS).includes(val), {
            message: i18n.t('errors:forms.required'),
          }),
        [FIELD_NAMES.NEW_TIMER_MINUTES]: z.coerce.number().optional(),
        [FIELD_NAMES.TIMER_DELAY]: z.coerce
          .number({
            invalid_type_error: i18n.t('errors:forms.requiredNumber', {
              field: t('checkIn:equipments.waitline.timerDelay'),
            }),
          })
          .min(minDelayMinutes, {
            message: i18n.t('errors:forms.minLengthCount', {
              field: t('checkIn:equipments.waitline.timerDelay'),
              count: String(minDelayMinutes),
            }),
          })
          .max(maxDelayMinutes, {
            message: i18n.t('errors:forms.maxLengthCount', {
              field: t('checkIn:equipments.waitline.timerDelay'),
              count: String(maxDelayMinutes),
            }),
          }),
        [FIELD_NAMES.SESSION_TYPE]: z.string().nullable(),
      })
      .superRefine((data, ctx) => {
        if (data[FIELD_NAMES.TIMER_OPTION] === TIMER_OPTIONS.NEW) {
          const val = data[FIELD_NAMES.NEW_TIMER_MINUTES]
          if (val == null) {
            ctx.addIssue({
              code: 'custom',
              message: i18n.t('errors:forms.requiredNumber', {
                field: t('checkIn:equipments.waitline.newTimer'),
              }),
              path: [FIELD_NAMES.NEW_TIMER_MINUTES],
            })
          } else if (val < minTimerMinutes) {
            ctx.addIssue({
              code: 'custom',
              message: i18n.t('errors:forms.minLengthCount', {
                field: t('checkIn:equipments.waitline.newTimer'),
                count: String(minTimerMinutes),
              }),
              path: [FIELD_NAMES.NEW_TIMER_MINUTES],
            })
          } else if (val > maxTimerMinutes) {
            ctx.addIssue({
              code: 'custom',
              message: i18n.t('errors:forms.maxLengthCount', {
                field: t('checkIn:equipments.waitline.newTimer'),
                count: String(maxTimerMinutes),
              }),
              path: [FIELD_NAMES.NEW_TIMER_MINUTES],
            })
          }
        }

        if (!isTestMode && hasMultipleSessionTypes && !data[FIELD_NAMES.SESSION_TYPE]) {
          ctx.addIssue({
            code: 'custom',
            message: i18n.t('errors:forms.required'),
            path: [FIELD_NAMES.SESSION_TYPE],
          })
        }
      })
      .passthrough()
  }, [equipment, hasMultipleSessionTypes, isTestMode, t])

  const formMethods = useForm({
    defaultValues: {
      ...DEFAULT_VALUES,
      [FIELD_NAMES.SESSION_TYPE]: equipment.siteSessionTypeIds?.[0] ?? null,
    },
    mode: 'onTouched',
    resolver: zodResolver(validationSchema),
  })

  const {
    handleSubmit,
    watch,
    formState: { isValid },
  } = formMethods

  const selectedOption = watch(FIELD_NAMES.TIMER_OPTION)

  const hasLatestAssignment = Boolean(latestAssignmentInEquipment)

  const [isFormReady, setIsFormReady] = useState(isTestMode)

  // After latest session loads, set the correct initial timer option and mark form ready
  useEffect(() => {
    if (isTestMode) return
    if (isLoadingAssignmentsForClient) return

    if (hasLatestAssignment) {
      formMethods.setValue(FIELD_NAMES.TIMER_OPTION, TIMER_OPTIONS.DEFAULT, {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
    }
    setIsFormReady(true)
  }, [isLoadingAssignmentsForClient, hasLatestAssignment, isTestMode, formMethods])

  useEffect(() => {
    if (selectedOption !== TIMER_OPTIONS.DEFAULT) return
    if (isLoadingAssignmentsForClient) return

    const defaultDelay = hasLatestAssignment
      ? Number(latestAssignmentInEquipment.delayMinutes)
      : Number(equipment.defaultDelayMinutes)

    formMethods.setValue(FIELD_NAMES.TIMER_DELAY, defaultDelay, {
      shouldTouch: false,
      shouldDirty: false,
      shouldValidate: true,
    })
  }, [
    selectedOption,
    isLoadingAssignmentsForClient,
    hasLatestAssignment,
    latestAssignmentInEquipment,
    equipment,
    formMethods,
  ])

  useEffect(() => {
    if (selectedOption !== TIMER_OPTIONS.NEW) return

    const defaultNewMinutes = hasLatestAssignment
      ? Number(latestAssignmentInEquipment.timerMinutes)
      : Number(equipment.defaultTimerMinutes)
    const defaultNewDelay = hasLatestAssignment
      ? Number(latestAssignmentInEquipment.delayMinutes)
      : Number(equipment.defaultDelayMinutes)

    formMethods.setValue(FIELD_NAMES.NEW_TIMER_MINUTES, defaultNewMinutes, {
      shouldTouch: false,
      shouldDirty: false,
      shouldValidate: true,
    })

    formMethods.setValue(FIELD_NAMES.TIMER_DELAY, defaultNewDelay, {
      shouldTouch: false,
      shouldDirty: false,
      shouldValidate: true,
    })
  }, [selectedOption, equipment, formMethods, hasLatestAssignment, latestAssignmentInEquipment])

  const handleSuccess = () => {
    if (isTestMode) {
      pushDialog(AssignEquipmentSuccessAlert, { isTestMode, onOkay: onSuccess })
    } else {
      pushDialog(AssignEquipmentSuccessAlert, { clientName })
    }
  }

  const submitAssignment = (timerMinutes, delayMinutes, selectedSessionTypeId) => {
    createAssignment(
      {
        siteId,
        clientId: isTestMode ? null : clientId,
        equipmentId,
        timerMinutes,
        delayMinutes,
        isOverride: false,
        checkInId: latestTanningCheckIn?.checkInId ?? null,
        siteSessionTypeId: selectedSessionTypeId,
      },
      {
        onSuccess: handleSuccess,
        onError: (error) => handleError(error, timerMinutes, delayMinutes, selectedSessionTypeId),
      }
    )
  }

  const handleError = (error, timerMinutes, delayMinutes, selectedSessionTypeId) => {
    const gqlError = error.response?.errors?.[0]
    const details = gqlError?.extensions?.details

    if (!isTestMode && details?.failReason === 'NO_CREDITS' && details?.serviceId) {
      const sessionTypeName = sessionTypes?.find(
        (st) => st.siteSessionTypeId === selectedSessionTypeId
      )?.name
      pushDialog(AddOnConfirmationDialog, {
        sessionTypeName,
        clientName,
        clientProfileImage: client?.profileImage?.url,
        serviceId: details.serviceId,
        serviceName: details.serviceName,
        servicePrice: details.servicePrice,
        serviceCurrency: details.serviceCurrency,
        serviceNextBillingDate: details.serviceNextBillingDate,
        siteId,
        clientId,
        equipmentId,
        siteSessionTypeId: selectedSessionTypeId,
        showOneTimeAddon: false,
      })
      return
    }

    if (!isTestMode && details?.failReason === 'ADD_ON_REQUIRED') {
      const sessionTypeName = sessionTypes?.find(
        (st) => st.siteSessionTypeId === selectedSessionTypeId
      )?.name
      pushDialog(AddOnConfirmationDialog, {
        addOnPrice: details.addOnPrice,
        addOnName: details.addOnName,
        sessionTypeName,
        clientName,
        clientProfileImage: client?.profileImage?.url,
        addonConfigId: details.addonConfigId,
        serviceId: details.serviceId,
        serviceName: details.serviceName,
        servicePrice: details.servicePrice,
        serviceCurrency: details.serviceCurrency,
        serviceNextBillingDate: details.serviceNextBillingDate,
        siteId,
        clientId,
        equipmentId,
        siteSessionTypeId: selectedSessionTypeId,
      })
      return
    }

    if (isTestMode) {
      pushDialog(TestEquipmentErrorAlert)
    } else {
      pushDialog(AssignEquipmentErrorAlert, {
        siteId,
        clientName,
        errorMessage: gqlError?.message,
        clientId,
        equipmentId,
        timerMinutes,
        delayMinutes,
      })
    }
  }

  const resolveTimerMinutes = (data) => {
    if (data[FIELD_NAMES.TIMER_OPTION] === TIMER_OPTIONS.NEW) {
      return Number(data[FIELD_NAMES.NEW_TIMER_MINUTES])
    }
    if (hasLatestAssignment) {
      return Number(latestAssignmentInEquipment?.timerMinutes)
    }
    return Number(equipment?.defaultTimerMinutes)
  }

  const onSubmit = (data) => {
    if (!equipmentId) return

    const timerMinutes = resolveTimerMinutes(data)
    const delayMinutes = Number(data[FIELD_NAMES.TIMER_DELAY])
    const selectedSessionTypeId = data[FIELD_NAMES.SESSION_TYPE]

    submitAssignment(timerMinutes, delayMinutes, selectedSessionTypeId)
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogLayout
          closeType={CLOSE_TYPES.ICON}
          title={
            isTestMode
              ? t('checkIn:equipments.detail.startTest')
              : t('checkIn:equipments.waitline.startTimerTitle')
          }
          size={SIZES.XS}
          primaryAction={{
            label: isTestMode
              ? t('checkIn:equipments.detail.startTest')
              : t('checkIn:equipments.waitline.assignAndStartTimer'),
            props: { type: 'submit', disabled: !isValid },
            isLoading,
          }}
          closeAction={popDialog}
        >
          <DialogContent sx={styles.dialogContent}>
            {!isFormReady || (!isTestMode && (isLoadingClient || isLoadingClientAssignments)) ? (
              <Loading />
            ) : (
              <>
                {isTestMode ? null : (
                  <Box sx={styles.personCard}>
                    <AvatarComponent
                      item={{
                        src: client?.profileImage?.url || 'https://via.placeholder.com/80',
                        alt: t('equipments.assignToEquipment.clientPhoto'),
                      }}
                      size={AVATAR_SIZE.SEARCH_RESULT}
                    />
                    <Box sx={styles.personInfo}>
                      <Box sx={styles.personHeader}>
                        <Box>
                          <Typography typography="header3" color="gray.100">
                            {clientName}
                          </Typography>
                          <Typography typography="medium" color="gray.64">
                            {client?.dateOfBirth &&
                              t('equipments.assignToEquipment.yearsOld', {
                                age: calculateAge(client.dateOfBirth),
                              })}
                          </Typography>
                        </Box>
                        <Box sx={styles.equipmentChipContainer}>
                          <Chip
                            label={equipment.name}
                            variant={CHIP_VARIANT.CONTAINED}
                            size={CHIP_SIZE.SMALL}
                          />
                        </Box>
                      </Box>
                      {isLoadingAssignmentsForClient && (
                        <Skeleton variant="text" width={100} height={24} />
                      )}
                      {parsedLastTanningDate && !isLoadingClientAssignments ? (
                        <Typography typography="small" color="gray.64">
                          {t('checkIn:equipments.waitline.lastTanningSession', {
                            date: format(parsedLastTanningDate, 'MMM dd, yyyy'),
                            time: format(parsedLastTanningDate, 'p'),
                          })}
                        </Typography>
                      ) : (
                        <Typography typography="small" color="gray.64">
                          {t('checkIn:equipments.waitline.noPreviousTanningFound')}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}

                <RadioGroup
                  id="timer-options"
                  label={t('checkIn:equipments.waitline.timerOptions')}
                  name={FIELD_NAMES.TIMER_OPTION}
                  radioType={RADIO_TYPES.BOX}
                  radioGroupStyles={styles.radioGroup}
                  labelStyles={styles.radioLabel}
                >
                  <RadioButton
                    value={TIMER_OPTIONS.DEFAULT}
                    label={t('checkIn:equipments.waitline.defaultTimer')}
                    helperText={
                      hasLatestAssignment
                        ? t('checkIn:equipments.waitline.lastUsedTime')
                        : t('checkIn:equipments.waitline.equipmentDefault')
                    }
                    sx={styles.radioButton}
                  />
                  <RadioButton
                    value={TIMER_OPTIONS.NEW}
                    label={t('checkIn:equipments.waitline.newTimer')}
                    helperText={t('checkIn:equipments.waitline.setNewTimer')}
                    sx={styles.radioButton}
                  />
                </RadioGroup>

                <Box sx={styles.fieldsRow}>
                  {selectedOption === TIMER_OPTIONS.NEW && (
                    <TextField
                      name={FIELD_NAMES.NEW_TIMER_MINUTES}
                      label={t('checkIn:equipments.waitline.newTimer')}
                      inputProps={{
                        type: 'number',
                        min: equipment?.minTimerMinutes,
                        max: equipment?.maxTimerMinutes,
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">{t('common:minutes')}</InputAdornment>
                        ),
                      }}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  <TextField
                    name={FIELD_NAMES.TIMER_DELAY}
                    label={t('checkIn:equipments.waitline.timerDelay')}
                    inputProps={{
                      type: 'number',
                      min: equipment?.minDelayMinutes ?? 0,
                      max: equipment?.maxDelayMinutes,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">{t('common:minutes')}</InputAdornment>
                      ),
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={selectedOption === TIMER_OPTIONS.NEW ? undefined : styles.delayFieldFull}
                  />
                </Box>

                {selectedOption === TIMER_OPTIONS.NEW && (
                  <Box sx={styles.infoBanner}>
                    <BannerAlert
                      severity={SEVERITY.INFO}
                      message={t('checkIn:equipments.waitline.newTimerLimitInfo', {
                        count: equipment.maxTimerMinutes,
                        minCount: equipment.minTimerMinutes,
                      })}
                    />
                  </Box>
                )}

                {!isTestMode && hasMultipleSessionTypes && (
                  <Dropdown
                    name={FIELD_NAMES.SESSION_TYPE}
                    label={t('checkIn:equipments.waitline.sessionType')}
                    options={
                      isLoadingSessionTypes
                        ? []
                        : sessionTypes.map((st) => ({
                            value: st.siteSessionTypeId,
                            label: st.name,
                          }))
                    }
                    shouldSearch
                  />
                )}
              </>
            )}
          </DialogContent>
        </DialogLayout>
      </form>
    </FormProvider>
  )
}

StartTimerDialog.propTypes = {
  clientId: PropTypes.string,
  equipmentId: PropTypes.string.isRequired,
  equipment: PropTypes.object.isRequired,
  clientName: PropTypes.string,
  isTestMode: PropTypes.bool,
  onSuccess: PropTypes.func,
}

export default StartTimerDialog
